import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { getSubs, toggleSub } from '../lib/api';
import { useAuth } from './AuthContext';

const SubsContext = createContext({ subs: new Set(), toggle: () => {}, loaded: false });

export const SubsProvider = ({ children }) => {
  const { user, loading } = useAuth();
  const [subs, setSubs] = useState(new Set());
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const ids = await getSubs();
      setSubs(new Set(ids));
    } catch (err) {
      console.error('[Subs] failed to load subscriptions:', err);
      setSubs(new Set());
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (loading) return;
    refresh();
  }, [loading, user, refresh]);

  const toggle = useCallback(async (channelId) => {
    // optimistic
    setSubs(prev => {
      const next = new Set(prev);
      if (next.has(channelId)) next.delete(channelId);
      else next.add(channelId);
      return next;
    });
    try {
      const res = await toggleSub(channelId);
      setSubs(prev => {
        const next = new Set(prev);
        if (res.subscribed) next.add(channelId);
        else next.delete(channelId);
        return next;
      });
    } catch (err) {
      console.error('[Subs] toggle failed:', err);
      refresh();
    }
  }, [refresh]);

  const value = useMemo(() => ({ subs, toggle, loaded }), [subs, toggle, loaded]);

  return (
    <SubsContext.Provider value={value}>
      {children}
    </SubsContext.Provider>
  );
};

export const useSubs = () => useContext(SubsContext);
